import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { cn } from '../../lib/utils';

interface DropdownMenuProps {
  children: React.ReactNode;
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  className?: string;
}

interface DropdownMenuContentProps {
  children: React.ReactNode;
  className?: string;
  align?: 'start' | 'end';
}

interface DropdownMenuItemProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

interface DropdownMenuSeparatorProps {
  className?: string;
}

const DropdownMenuContext = React.createContext<{
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dropdownId: string;
}>({
  isOpen: false,
  setIsOpen: () => {},
  dropdownId: '',
});

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownId = useRef(`dropdown-${Math.random().toString(36).substr(2, 9)}`);

  return (
    <DropdownMenuContext.Provider value={{ isOpen, setIsOpen, dropdownId: dropdownId.current }}>
      {children}
    </DropdownMenuContext.Provider>
  );
};

export const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({ children, className }) => {
  const { isOpen, setIsOpen, dropdownId } = React.useContext(DropdownMenuContext);

  return (
    <button
      className={cn(className)}
      onClick={() => setIsOpen(!isOpen)}
      type="button"
      data-dropdown-trigger="true"
      data-dropdown-id={dropdownId}
    >
      {children}
    </button>
  );
};

export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({ 
  children, 
  className,
  align = 'end' 
}) => {
  const { isOpen, setIsOpen, dropdownId } = React.useContext(DropdownMenuContext);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });

  const calculatePosition = React.useCallback(() => {
    if (!triggerRef.current) return { top: 0, left: 0 };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownWidth = 200; // Width of the dropdown
    const dropdownHeight = ref.current?.offsetHeight || 150; // Use actual height if available
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Default to showing on the left side since we're at the right edge of the table
    let left = Math.max(8, triggerRect.left - dropdownWidth + triggerRect.width);
    
    // If not enough space on the left, try the right side
    if (left < 8) {
      left = Math.min(triggerRect.right, viewportWidth - dropdownWidth - 8);
    }

    // Vertical positioning
    let top = triggerRect.bottom + 4;
    
    // If not enough space below, position above
    if (top + dropdownHeight > viewportHeight - 8) {
      top = Math.max(8, triggerRect.top - dropdownHeight - 4);
    }

    return { top, left };
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  // Find trigger and calculate initial position
  useEffect(() => {
    if (isOpen) {
      const trigger = document.querySelector(`[data-dropdown-id="${dropdownId}"]`);
      if (trigger) {
        triggerRef.current = trigger as HTMLElement;
        setPosition(calculatePosition());
      }
    }
  }, [isOpen, dropdownId, calculatePosition]);

  // Update position on window resize
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      setPosition(calculatePosition());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, calculatePosition]);

  // Update position after render to account for actual dropdown size
  useLayoutEffect(() => {
    if (isOpen && ref.current) {
      setPosition(calculatePosition());
    }
  }, [isOpen, calculatePosition]);

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      className={cn(
        'fixed z-[9999] min-w-[8rem] rounded-md border bg-white p-1 shadow-md',
        className
      )}
      style={{
        top: position.top,
        left: position.left
      }}
    >
      {children}
    </div>
  );
};

export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({ children, className, onClick }) => {
  const { setIsOpen } = React.useContext(DropdownMenuContext);

  const handleClick = () => {
    onClick?.();
    setIsOpen(false);
  };

  return (
    <button
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100',
        className
      )}
      onClick={handleClick}
      type="button"
    >
      {children}
    </button>
  );
};

export const DropdownMenuSeparator: React.FC<DropdownMenuSeparatorProps> = ({ className }) => {
  return (
    <div className={cn('-mx-1 my-1 h-px bg-gray-100', className)} />
  );
}; 